import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

function decodeHtml(text) {
  return String(text || '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

function fmtMoney(value) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value || 0));
}

function fmtMoneyOrDash(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || Math.round(n * 100) === 0) return '—';
  return fmtMoney(n);
}

function fmtPct(value) {
  return `${Number(value || 0).toFixed(1)}%`;
}

function buildCategoryPathMap(categories = []) {
  const byId = new Map(
    categories.map((c) => [Number(c.id), { id: Number(c.id), name: decodeHtml(c.name), parent: Number(c.parent || 0) }])
  );
  const cache = new Map();
  const pathFor = (categoryId) => {
    const id = Number(categoryId);
    if (!id || cache.has(id)) return cache.get(id) || '';
    const parts = [];
    let cur = byId.get(id);
    const visited = new Set();
    while (cur && !visited.has(cur.id)) {
      visited.add(cur.id);
      parts.unshift(cur.name);
      cur = cur.parent ? byId.get(cur.parent) : null;
    }
    const path = parts.join(' > ');
    cache.set(id, path);
    return path;
  };
  return { pathFor };
}

const PRODUCT_HEADERS = [
  'Category path',
  'Category',
  'Product ID',
  'Product',
  'Unique sponsors',
  'Non-pledge donations',
  'Pledge amount',
  'Paid',
  'Pledge balance',
  'Total revenue',
  '% Pledge paid',
];

const DONOR_HEADERS = [
  'Sponsor',
  'Category path',
  'Category',
  'Product ID',
  'Product',
  'Non-pledge',
  'Pledge amount',
  'Paid',
  'Pledge balance',
  'Total revenue',
];

function mapProductRows(exportData) {
  const { pathFor } = buildCategoryPathMap(exportData?.categories || []);
  return (exportData?.product_totals || []).map((r) => {
    const catId = Number(r.category_id);
    const path = pathFor(catId);
    return [
      path,
      decodeHtml(r.category_name),
      String(r.product_id),
      decodeHtml(r.product_name),
      Number(r.unique_sponsors || 0),
      fmtMoneyOrDash(r.non_pledge_donations),
      fmtMoney(r.pledge_amount),
      fmtMoney(r.paid_amount),
      fmtMoney(r.pledge_balance),
      fmtMoney(r.total_revenue),
      fmtPct(r.pct_pledge_paid),
    ];
  });
}

function mapDonorRows(exportData) {
  const { pathFor } = buildCategoryPathMap(exportData?.categories || []);
  return (exportData?.donor_details || []).map((r) => {
    const catId = Number(r.category_id);
    return [
      decodeHtml(r.sponsor_name),
      pathFor(catId),
      decodeHtml(r.category_name),
      String(r.product_id),
      decodeHtml(r.product_name),
      fmtMoneyOrDash(r.non_pledge_donations),
      fmtMoney(r.pledge_amount),
      fmtMoney(r.paid_amount),
      fmtMoney(r.pledge_balance),
      fmtMoney(r.total_revenue),
    ];
  });
}

function autoColWidths(headers, rows) {
  return headers.map((header, idx) => {
    const values = rows.map((row) => String(row[idx] ?? ''));
    const maxLen = values.reduce((max, v) => Math.max(max, v.length), header.length);
    return { wch: Math.min(Math.max(maxLen + 2, 12), 48) };
  });
}

export function exportKotilingaTreeRevenueExcel(exportData) {
  const productRows = mapProductRows(exportData);
  const donorRows = mapDonorRows(exportData);
  const generated = new Date();
  const meta = exportData?.meta || {};

  const productSheet = XLSX.utils.aoa_to_sheet([
    ['Kotilinga Tree Revenue — Product totals (Kadambavanam scope)'],
    [`Generated: ${generated.toLocaleString()}`],
    [`Products: ${meta.total_products ?? productRows.length}`],
    [],
    PRODUCT_HEADERS,
    ...productRows,
  ]);
  productSheet['!cols'] = autoColWidths(PRODUCT_HEADERS, productRows);

  const donorSheet = XLSX.utils.aoa_to_sheet([
    ['Kotilinga Tree Revenue — Donor / order line details'],
    [`Generated: ${generated.toLocaleString()}`],
    [`Donor lines: ${meta.total_donor_lines ?? donorRows.length}`],
    [],
    DONOR_HEADERS,
    ...donorRows,
  ]);
  donorSheet['!cols'] = autoColWidths(DONOR_HEADERS, donorRows);

  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, productSheet, 'Product totals');
  XLSX.utils.book_append_sheet(book, donorSheet, 'Donor details');
  XLSX.writeFile(book, `kotilinga-tree-revenue-${generated.toISOString().slice(0, 10)}.xlsx`);
}

export function exportKotilingaTreeRevenuePdf(exportData) {
  const productRows = mapProductRows(exportData);
  const donorRows = mapDonorRows(exportData);
  const meta = exportData?.meta || {};
  const generated = new Date();

  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const margin = 36;
  let y = 32;

  doc.setFontSize(14);
  doc.text('Kotilinga Tree Revenue & Pledge Report', margin, y);
  y += 16;
  doc.setFontSize(9);
  doc.text(`Kadambavanam scope · Generated ${generated.toLocaleString()}`, margin, y);
  y += 12;
  doc.text(
    `Products: ${Number(meta.total_products || productRows.length).toLocaleString()} · Donor rows: ${Number(meta.total_donor_rows || donorRows.length).toLocaleString()}`,
    margin,
    y
  );
  y += 16;

  doc.setFontSize(11);
  doc.text('Product totals', margin, y);
  y += 8;
  autoTable(doc, {
    startY: y,
    head: [PRODUCT_HEADERS],
    body: productRows,
    styles: { fontSize: 7, cellPadding: 3 },
    headStyles: { fillColor: [0, 105, 120] },
    margin: { left: margin, right: margin },
  });
  y = (doc.lastAutoTable?.finalY || y) + 20;

  if (y > doc.internal.pageSize.getHeight() - 80) {
    doc.addPage();
    y = 36;
  }
  doc.setFontSize(11);
  doc.text('Donor details (by product)', margin, y);
  y += 8;
  autoTable(doc, {
    startY: y,
    head: [DONOR_HEADERS],
    body: donorRows,
    styles: { fontSize: 6, cellPadding: 2 },
    headStyles: { fillColor: [70, 90, 100] },
    margin: { left: margin, right: margin },
  });

  doc.save(`kotilinga-tree-revenue-${generated.toISOString().slice(0, 10)}.pdf`);
}
